import {FC, useMemo} from 'react';
import {Box, chakra, Flex, Grid} from '@chakra-ui/react';

import {isSameDay, MONTHS, startOfDay, toValueString, WEEKDAYS} from '../../DateTimePicker.utils';
import {NavButton} from '../NavButton';

interface IProps {
  viewYear: number;
  viewMonth: number;
  selected: Date | null;
  minDate: Date | null;
  onDayClick: (day: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

export const Calendar: FC<IProps> = ({
  viewYear,
  viewMonth,
  selected,
  minDate,
  onDayClick,
  onPrevMonth,
  onNextMonth
}) => {
  const cells = useMemo(() => {
    const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
    const gridStart = new Date(viewYear, viewMonth, 1 - firstWeekday);
    return Array.from({length: 42}, (_, i) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);
      return date;
    });
  }, [viewYear, viewMonth]);

  return (
    <>
      <Flex align={'center'} justify={'space-between'} mb={'8px'}>
        <NavButton onClick={onPrevMonth} icon={'ArrowThinLeft'} />
        <Box fontWeight={600} color={'{colors.textPrimary}'}>
          {MONTHS[viewMonth]} {viewYear}
        </Box>
        <NavButton onClick={onNextMonth} icon={'ArrowThinLeft'} isNext />
      </Flex>

      <Grid gridTemplateColumns={'repeat(7, 1fr)'} textAlign={'center'}>
        {WEEKDAYS.map((wd) => (
          <Box
            key={wd}
            py={'4px'}
            fontSize={'12px'}
            fontWeight={600}
            color={'{colors.textTertiary}'}
          >
            {wd}
          </Box>
        ))}

        {cells.map((day) => {
          const inMonth = day.getMonth() === viewMonth;
          const isSelected = selected ? isSameDay(day, selected) : false;
          const isToday = isSameDay(day, new Date());
          const isDisabled = minDate ? startOfDay(day) < startOfDay(minDate) : false;

          return (
            <chakra.button
              type={'button'}
              key={toValueString(day)}
              w={'36px'}
              mx={'auto'}
              py={'8px'}
              fontSize={'14px'}
              borderRadius={'6px'}
              cursor={isDisabled ? 'not-allowed' : 'pointer'}
              opacity={isDisabled ? 0.4 : inMonth ? 1 : 0.5}
              color={isSelected ? '{colors.white}' : '{colors.textPrimary}'}
              background={
                isSelected ? '{colors.textPrimary}' : isToday ? '{colors.blue_01}' : 'transparent'
              }
              _hover={{background: isSelected ? '{colors.textPrimary}' : '{colors.bgSurface}'}}
              disabled={isDisabled}
              onClick={() => onDayClick(day)}
            >
              {day.getDate()}
            </chakra.button>
          );
        })}
      </Grid>
    </>
  );
};
